import { XObjectType, X_OBJECT_TYPE } from '../..';
import { AJV_EXTENSION_REF_SCHEMA } from '../../globals';
import {
    ISchemaAnyOf,
    ISchemaArray,
    ISchemaCoreType,
    ISchemaObject,
    ISchemaObjectProperties,
    ISchemaOneOf,
    ISchemaRecord,
    ISchemaRecordProperties,
    ISchemaRefFormatted,
    ISchemaTuple,
    ISchemaType,
    StringKeys,
} from '../properties';
import { SchemaStringFormat, SchemaTypeName } from '../properties/primitives';

/**  Types  */
interface ISchemaTypescriptObjectProperty {
    property_name: string;
    value: string;
    is_required: boolean;
}

/**  Globals  */
const PRIMITIVES: Record<
    Exclude<SchemaTypeName, SchemaTypeName.array | SchemaTypeName.object>,
    string
> = {
    [SchemaTypeName.number]: 'number',
    [SchemaTypeName.integer]: 'number',
    [SchemaTypeName.string]: 'string',
    [SchemaTypeName.boolean]: 'boolean',
    [SchemaTypeName.null]: 'null',
};

enum DEFAULT_ADDITIONAL_PRIMITIVES {
    'Date' = 'Date',
}

const OUTPUT_PREFIX = `/**
 * This file was automatically generated by @nicejob-library/schema.
 * DO NOT MODIFY THIS FILE.
 */
`;

/**
 * RULES
 * - If nullable, not required
 */
export function toTypescript<Properties extends ISchemaObjectProperties<StringKeys<Properties>>>(
    {
        name,
        schema,
    }: {
        name: string | null;
        schema: Omit<ISchemaObject<Properties, StringKeys<Properties>>, 'additionalProperties'>;
    },
    { spacing } = {
        spacing: 0,
    }
): string {
    /**
     * This object
     */
    const properties: Array<ISchemaTypescriptObjectProperty> = [];

    for (const property_name in schema.properties) {
        const is_required = schema.required.includes(property_name);
        const property_schema = schema.properties[property_name];

        const { value } = determinePropertyValue({
            property_schema,
            spacing,
        });

        properties.push({
            property_name,
            value,
            is_required,
        });
    }

    const stringified_typescript_schema = stringifyObjectType({ name, properties, spacing });

    /**
     * Return
     */

    //  No name? No validation
    if (!name) {
        return stringified_typescript_schema;
    }

    return `${OUTPUT_PREFIX}${stringified_typescript_schema}`;
}

/**
 * Return a Typescript-compatible string representation of a value type
 */
function determinePropertyValue({
    property_schema,
    spacing,
}: {
    property_schema: ISchemaType;
    spacing: number;
}): {
    value: string;
} {
    const property_schema_ref = property_schema as ISchemaRefFormatted;
    const property_schema_any_of = property_schema as ISchemaAnyOf<ISchemaCoreType>;
    const property_schema_one_of = property_schema as ISchemaOneOf<ISchemaCoreType>;
    const property_schema_singular = property_schema as ISchemaCoreType;
    const property_schema_object = property_schema as ISchemaObject<any, string>;
    const property_schema_record = property_schema as ISchemaRecord<any>;
    const property_schema_array = property_schema as ISchemaArray;
    const property_schema_tuple = property_schema as ISchemaTuple;

    //  -: $ref
    if (property_schema_ref[AJV_EXTENSION_REF_SCHEMA]) {
        return determinePropertyValue({
            property_schema: property_schema_ref[AJV_EXTENSION_REF_SCHEMA],
            spacing,
        });
    }

    //  A: Union type
    if (property_schema_any_of.anyOf) {
        const value = Array.from(
            new Set(
                property_schema_any_of.anyOf.map(
                    type =>
                        determinePropertyValue({
                            property_schema: type,
                            spacing,
                        }).value
                )
            )
        ).join(' | ');

        return {
            value,
        };
    } else if (property_schema_one_of.oneOf) {
        const value = Array.from(
            new Set(
                property_schema_one_of.oneOf.map(
                    type =>
                        determinePropertyValue({
                            property_schema: type,
                            spacing,
                        }).value
                )
            )
        ).join(' | ');

        return {
            value,
        };
    }

    //  B1: Nested object
    else if (
        property_schema_singular.type === SchemaTypeName.object &&
        property_schema_singular[X_OBJECT_TYPE] === XObjectType.OBJECT
    ) {
        return {
            //  Use `any` type to overcome Typescript's concern with recursion; it's not an issue
            value: toTypescript<any>(
                {
                    name: null,
                    schema: property_schema_object,
                },
                {
                    spacing: spacing + 4,
                }
            ),
        };
    }

    //  B2: Nested record
    else if (property_schema_singular.type === SchemaTypeName.object) {
        const value_types: Array<ISchemaRecordProperties[string]> = [];

        for (const k in property_schema_record.patternProperties || {}) {
            value_types.push(property_schema_record.patternProperties[k]);
        }

        const values =
            value_types
                .map(t => determinePropertyValue({ property_schema: t, spacing }))
                .map(v => v.value)
                .join(' | ') || 'unknown';

        return {
            value: `Record<string, ${values}>`,
        };
    }

    //  C1: Nested tuple
    else if (
        property_schema_singular.type === SchemaTypeName.array &&
        property_schema_tuple.prefixItems
    ) {
        const tuple_values = property_schema_tuple.prefixItems.map(item =>
            determinePropertyValue({
                property_schema: item,
                spacing,
            })
        );

        return {
            value: `[${tuple_values.map(v => v.value).join(', ')}]`,
        };
    }

    //  C2: Nested array
    else if (property_schema_singular.type === SchemaTypeName.array) {
        const item_value = determinePropertyValue({
            property_schema: property_schema_array.items,
            spacing,
        });

        return {
            value: `Array<${item_value.value}>`,
        };
    }

    //  D: Enum
    else if (
        property_schema_singular.type === SchemaTypeName.string &&
        property_schema_singular.format !== SchemaStringFormat.date_time &&
        (property_schema_singular.const || property_schema_singular.enum?.length)
    ) {
        //  Push new enum
        const values = property_schema_singular.const
            ? [property_schema_singular.const]
            : property_schema_singular.enum
            ? property_schema_singular.enum
            : []; // Shouldn't be a case; TS acting up

        return {
            value: values.map(v => `'${v.replace(/'/g, `\\'`)}'`).join(' | '),
        };
    }

    //  E: Date
    else if (
        property_schema_singular.type === SchemaTypeName.string &&
        property_schema_singular.format === SchemaStringFormat.date_time
    ) {
        return {
            value: DEFAULT_ADDITIONAL_PRIMITIVES.Date,
        };
    }

    //  n: Default Typescript scalar
    else {
        return {
            value: PRIMITIVES[property_schema_singular.type],
        };
    }
}

/**
 * Stringify: Object type
 */
function stringifyObjectType({
    name,
    properties,
    spacing,
}: {
    name: string | null;
    properties: Array<ISchemaTypescriptObjectProperty>;
    spacing: number;
}): string {
    const space = ' '.repeat(spacing);

    const prefix = name ? `export interface ${name} ` : '';

    return `${prefix}{
    ${properties
        .map(({ property_name, value, is_required }) =>
            is_required ? `${property_name}: ${value};` : `${property_name}?: ${value};`
        )
        .join('\n    ')}
}`.replace(/\n/g, `\n${space}`);
}
